// Connecteurs (Matomo, Jira, Google Analytics 4, PostHog, Lookback).
//
// Un connecteur de mesure importe des MESURES de l'outil vers une triangulation
// dédiée, où on les confronte aux verbatims. Lookback, lui, apporte des
// SÉANCES, importées dans une étude (voir `LookbackConnector.tsx`). Les identifiants d'accès sont chiffrés
// côté serveur et ne reviennent JAMAIS au navigateur : en modification, un
// champ secret vide veut dire « inchangé ».
import { useCallback, useState, type FormEvent } from 'react';
import {
  useConnectorCatalog,
  useConnectors,
  useDeleteConnector,
  useProductContext,
  useSaveConnector,
  useSyncConnector,
  useTestConnector,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Connector, ConnectorKind, ConnectorMetric, ConnectorType, FunnelStep } from '../api/types';
import { Button, buttonClass, ErrorBanner, Eyebrow, Field, Note, Spinner } from '../components/ui';
import { Link } from '../router';
import { Drawer } from '../write/Drawer';
import { LookbackCreateForm, LookbackReturnNotice, LookbackRow } from './LookbackConnector';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const fmtValue = (m: ConnectorMetric) =>
  `${m.value.toLocaleString('fr-FR')}${m.unit === '%' ? ' %' : m.unit === 's' ? ' s' : ` ${m.unit}`}`;

export function ConnectorsSection({ projectId }: { projectId: string }) {
  const list = useConnectors(projectId);
  const catalog = useConnectorCatalog();
  const [editing, setEditing] = useState<{ kind: ConnectorKind; connector?: Connector } | null>(null);
  const close = useCallback(() => setEditing(null), []);

  const types = catalog.data ?? [];
  const typeOf = (kind: ConnectorKind) => types.find((t) => t.kind === kind);

  return (
    <section className="zy-card opt-section" aria-labelledby="conn-title">
      <div className="opt-head">
        <div>
          <Eyebrow>Sources de mesure</Eyebrow>
          <h2 className="t-h3" id="conn-title">Connecteurs</h2>
          <p className="card-lede">
            Importez les mesures de vos outils dans la triangulation, pour les confronter à ce que disent
            les utilisateurs, et les séances Lookback dans vos études. Les identifiants sont chiffrés et ne
            sont jamais réaffichés.
          </p>
        </div>
      </div>
      <LookbackReturnNotice />

      {list.isPending || catalog.isPending ? (
        <Spinner label="Chargement des connecteurs…" />
      ) : list.isError ? (
        <ErrorBanner>{errorMessage(list.error)}</ErrorBanner>
      ) : (
        <>
          {!list.data.secretsReady ? (
            <Note tone="avertissement">
              Le stockage chiffré des identifiants n’est pas configuré sur ce serveur : aucun connecteur ne
              peut être enregistré pour l’instant.
            </Note>
          ) : null}

          {list.data.connectors.length > 0 ? (
            <ul className="conn-list">
              {list.data.connectors.map((c) => (
                c.capability === 'sessions' ? (
                  <LookbackRow key={c.id} projectId={projectId} connector={c} canManage={list.data.canManage} />
                ) : (
                  <ConnectorRow
                    key={c.id}
                    projectId={projectId}
                    connector={c}
                    typeLabel={typeOf(c.kind)?.label ?? c.kind}
                    canManage={list.data.canManage}
                    onEdit={() => setEditing({ kind: c.kind, connector: c })}
                  />
                )
              ))}
            </ul>
          ) : (
            <p className="zy-texte-muet">Aucun connecteur pour l’instant.</p>
          )}

          {list.data.canManage && list.data.secretsReady ? (
            <div className="conn-add">
              <p className="opt-subtitle">Ajouter un connecteur</p>
              <div className="conn-types">
                {types.map((t) => (
                  <button key={t.kind} type="button" className="conn-type" onClick={() => setEditing({ kind: t.kind })}>
                    <span className="conn-type-name">{t.label}</span>
                    <span className="conn-type-desc">{DESCRIPTIONS[t.kind]}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : !list.data.canManage ? (
            <Note tone="lecture">La configuration des connecteurs est réservée aux administrateurs.</Note>
          ) : null}
        </>
      )}

      <Drawer
        open={editing !== null}
        title={editing?.connector ? `Modifier · ${editing.connector.label}` : `Nouveau connecteur · ${editing ? typeOf(editing.kind)?.label ?? '' : ''}`}
        description={editing && typeOf(editing.kind)?.capability === 'sessions'
          ? 'Connectez le compte Lookback, puis importez ses séances dans une étude.'
          : 'Renseignez l’accès à l’outil, testez la connexion, puis enregistrez.'}
        onClose={close}
      >
        {editing && typeOf(editing.kind)?.capability === 'sessions' ? (
          <LookbackCreateForm projectId={projectId} />
        ) : editing && typeOf(editing.kind) ? (
          <ConnectorForm
            projectId={projectId}
            type={typeOf(editing.kind)!}
            {...(editing.connector ? { existing: editing.connector } : {})}
            onDone={close}
          />
        ) : null}
      </Drawer>
    </section>
  );
}

const DESCRIPTIONS: Record<ConnectorKind, string> = {
  matomo: 'Visites, visiteurs, rebond, usage par fonctionnalité.',
  jira: 'Bugs ouverts, créés, résolus ; tickets par fonctionnalité.',
  ga4: 'Utilisateurs actifs, sessions, engagement, vues par page.',
  posthog: 'Utilisateurs actifs, stickiness, taux d’adoption par fonctionnalité.',
  lookback: 'Séances de tests et d’entretiens : transcriptions importées dans une étude.',
};

function ConnectorRow({
  projectId, connector: c, typeLabel, canManage, onEdit,
}: {
  projectId: string; connector: Connector; typeLabel: string; canManage: boolean; onEdit: () => void;
}) {
  const sync = useSyncConnector(projectId);
  const remove = useDeleteConnector(projectId);
  const [confirm, setConfirm] = useState(false);
  const imported = sync.data?.metrics;

  return (
    <li className="conn-row">
      <div className="conn-row-head">
        <div>
          <p className="conn-name">
            {c.label}
            <span className="conn-kind">{typeLabel}</span>
          </p>
          <p className="conn-meta">
            {c.lastSyncAt
              ? c.lastStatus === 'ok'
                ? `Dernier import le ${fmtDate(c.lastSyncAt)} · ${c.lastCount ?? 0} mesure${(c.lastCount ?? 0) > 1 ? 's' : ''}`
                : `Échec le ${fmtDate(c.lastSyncAt)}`
              : 'Jamais synchronisé'}
            {` · période : ${c.periodDays} jours`}
          </p>
        </div>
        <span className={`conn-status conn-status-${c.lastStatus ?? 'none'}`}>
          {c.lastStatus === 'ok' ? 'Connecté' : c.lastStatus === 'error' ? 'En erreur' : 'À synchroniser'}
        </span>
      </div>

      {c.lastStatus === 'error' && c.lastError ? <p className="conn-error">{c.lastError}</p> : null}

      <div className="conn-actions">
        <Button variant="primary" loading={sync.isPending} onClick={() => sync.mutate(c.id)}>
          Synchroniser
        </Button>
        {c.triangulationId || sync.data?.triangulationId ? (
          <Link to={`/projet/${encodeURIComponent(projectId)}/triangulation`} className={buttonClass('ghost')}>
            Voir dans la triangulation
          </Link>
        ) : null}
        {canManage ? (
          <>
            <Button variant="ghost" onClick={onEdit}>Modifier</Button>
            {confirm ? (
              <>
                <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(c.id)}>
                  Confirmer la suppression
                </Button>
                <Button variant="link" size="s" type="button" onClick={() => setConfirm(false)}>Annuler</Button>
              </>
            ) : (
              <Button variant="link-danger" size="s" type="button" onClick={() => setConfirm(true)}>Supprimer</Button>
            )}
          </>
        ) : null}
      </div>
      {confirm ? (
        <p className="zy-texte-muet">Les mesures déjà importées restent dans la triangulation.</p>
      ) : null}

      {sync.error ? <ErrorBanner>{errorMessage(sync.error)}</ErrorBanner> : null}
      {imported && imported.length > 0 ? (
        <div className="conn-imported" role="status">
          <p className="opt-subtitle">{imported.length} mesure{imported.length > 1 ? 's' : ''} importée{imported.length > 1 ? 's' : ''}</p>
          <ul>
            {imported.map((m) => (
              <li key={m.key}>
                <span>{m.name}</span>
                <strong>{fmtValue(m)}</strong>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

function ConnectorForm({
  projectId, type, existing, onDone,
}: {
  projectId: string; type: ConnectorType; existing?: Connector; onDone: () => void;
}) {
  const save = useSaveConnector(projectId);
  const testConn = useTestConnector(projectId);
  const context = useProductContext(projectId);
  const [label, setLabel] = useState(existing?.label ?? type.label);
  const [periodDays, setPeriodDays] = useState(String(existing?.periodDays ?? 30));
  const [config, setConfig] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const f of type.fields) out[f.key] = String(existing?.config?.[f.key] ?? '');
    return out;
  });
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [featureMap, setFeatureMap] = useState<Record<string, string>>(existing?.config?.featureMap ?? {});
  // L'entonnoir (PostHog) : des étapes ORDONNÉES, de 2 à 6.
  const [funnel, setFunnel] = useState<FunnelStep[]>(existing?.config?.funnel ?? []);

  const secretsFilled = type.secretFields.every((f) => (secrets[f.key] ?? '').trim());
  // En modification, laisser les secrets vides = garder ceux enregistrés.
  const secretsOk = existing ? secretsFilled || type.secretFields.every((f) => !(secrets[f.key] ?? '').trim()) : secretsFilled;
  const configOk = type.fields.every((f) => f.key === 'bugType' || (config[f.key] ?? '').trim());

  const body = () => ({
    kind: type.kind,
    label: label.trim(),
    periodDays: Number(periodDays) || 30,
    config: { ...config, featureMap, ...(type.supportsFunnel ? { funnel: funnel.filter((s) => s.pattern.trim()) } : {}) },
    ...(secretsFilled ? { secrets } : {}),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync(existing ? { id: existing.id, ...body() } : body());
      onDone();
    } catch {
      /* rendu par le bandeau */
    }
  }

  const features = context.data?.features ?? [];

  return (
    <form className="write-form" onSubmit={submit} noValidate>
      <Field label="Nom du connecteur" value={label} onChange={(e) => setLabel(e.target.value)} />
      {type.fields.map((f) => (
        <div key={f.key}>
          <Field
            label={f.label}
            value={config[f.key] ?? ''}
            placeholder={f.placeholder}
            onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
            voice={false}
            // Valeurs connues (PostHog Cloud US / EU) : proposées, pas imposées —
            // une instance auto-hébergée se tape librement.
            {...(f.suggestions ? { list: `sugg-${type.kind}-${f.key}` } : {})}
          />
          {f.suggestions ? (
            <datalist id={`sugg-${type.kind}-${f.key}`}>
              {f.suggestions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          ) : null}
        </div>
      ))}

      <div className="conn-secrets">
        <p className="opt-subtitle">Identifiants d’accès {existing?.hasSecret ? '· enregistrés' : ''}</p>
        {existing?.hasSecret ? (
          <p className="zy-texte-muet">Laissez vide pour conserver les identifiants enregistrés ; ils ne sont jamais réaffichés.</p>
        ) : null}
        {type.secretFields.map((f) =>
          f.multiline ? (
            <div className="zy-field" key={f.key}>
              <label className="zy-field__label" htmlFor={`sec-${f.key}`}>{f.label}</label>
              <textarea
                id={`sec-${f.key}`}
                className="zy-input conn-secret-area"
                rows={4}
                autoComplete="off"
                spellCheck={false}
                value={secrets[f.key] ?? ''}
                onChange={(e) => setSecrets((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder="Collez le contenu du fichier JSON"
              />
            </div>
          ) : (
            <Field
              key={f.key}
              label={f.label}
              type={f.key === 'email' ? 'email' : 'password'}
              autoComplete="off"
              value={secrets[f.key] ?? ''}
              onChange={(e) => setSecrets((s) => ({ ...s, [f.key]: e.target.value }))}
            />
          ),
        )}
      </div>

      <Field
        label="Période mesurée (jours)"
        type="number"
        min={1}
        max={365}
        value={periodDays}
        onChange={(e) => setPeriodDays(e.target.value)}
      />

      <div className="conn-features">
        <p className="opt-subtitle">Mesure par fonctionnalité (facultatif)</p>
        {features.length === 0 ? (
          <p className="zy-texte-muet">Décrivez les fonctionnalités dans la mesure 0 pour pouvoir les mesurer une à une.</p>
        ) : (
          <>
            <p className="zy-texte-muet">{type.featureHint}. Laissez vide pour ne pas mesurer une fonctionnalité.</p>
            <ul className="conn-feature-list">
              {features.map((f) => (
                <li key={f.id}>
                  <label htmlFor={`fm-${f.id}`}>{f.name}</label>
                  <input
                    id={`fm-${f.id}`}
                    className="zy-input"
                    value={featureMap[f.id] ?? ''}
                    onChange={(e) => setFeatureMap((m) => ({ ...m, [f.id]: e.target.value }))}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {type.supportsFunnel ? <FunnelEditor steps={funnel} onChange={setFunnel} hint={type.featureHint} /> : null}

      {testConn.data ? (
        <Note tone="succes" className="conn-result" role="status">
          <p><strong>Connexion réussie.</strong> Aperçu :</p>
          <ul>
            {testConn.data.sample.map((m) => (
              <li key={m.key}>{m.name} : {fmtValue(m)}</li>
            ))}
          </ul>
        </Note>
      ) : null}
      {testConn.error ? <ErrorBanner>{errorMessage(testConn.error)}</ErrorBanner> : null}
      {save.error ? <ErrorBanner>{errorMessage(save.error)}</ErrorBanner> : null}

      <div className="m0-actions">
        <Button
          type="button"
          variant="ghost"
          loading={testConn.isPending}
          disabled={!configOk || (!secretsFilled && !existing)}
          onClick={() => testConn.mutate({ ...body(), ...(existing ? { connectorId: existing.id } : {}) })}
        >
          Tester la connexion
        </Button>
        <Button type="submit" variant="accent" loading={save.isPending} disabled={!configOk || !secretsOk || !label.trim()}>
          {existing ? 'Enregistrer' : 'Créer le connecteur'}
        </Button>
      </div>
    </form>
  );
}

// --- Entonnoir (PostHog) ----------------------------------------------------------

const FUNNEL_MAX = 6;

/** Les étapes d'un entonnoir, dans l'ordre où l'utilisateur les franchit. */
function FunnelEditor({ steps, onChange, hint }: { steps: FunnelStep[]; onChange: (s: FunnelStep[]) => void; hint: string }) {
  const set = (i: number, patch: Partial<FunnelStep>) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const kept = steps.filter((s) => s.pattern.trim()).length;
  return (
    <div className="conn-features">
      <p className="opt-subtitle">Entonnoir de conversion (facultatif)</p>
      <p className="zy-texte-muet">
        Les étapes d’un parcours clé, dans l’ordre (ex. inscription → connexion bancaire → premier budget).
        Chaque synchro mesure la part des utilisateurs qui atteignent chaque étape, et où ils abandonnent.
        De 2 à {FUNNEL_MAX} étapes. {hint}.
      </p>
      {steps.length > 0 ? (
        <ol className="conn-feature-list conn-funnel">
          {steps.map((s, i) => (
            <li key={i}>
              <input className="zy-input" aria-label={`Nom de l’étape ${i + 1}`} placeholder={`Étape ${i + 1}`}
                value={s.name} onChange={(e) => set(i, { name: e.target.value })} />
              <input className="zy-input" aria-label={`Motif de l’étape ${i + 1}`} placeholder="event:signup ou /chemin"
                value={s.pattern} onChange={(e) => set(i, { pattern: e.target.value })} />
              <Button variant="link-danger" size="s" type="button" onClick={() => onChange(steps.filter((_, j) => j !== i))}>Retirer</Button>
            </li>
          ))}
        </ol>
      ) : null}
      {steps.length < FUNNEL_MAX ? (
        <div>
          <Button type="button" variant="ghost" onClick={() => onChange([...steps, { name: '', pattern: '' }])}>
            Ajouter une étape
          </Button>
        </div>
      ) : null}
      {kept === 1 ? <p className="zy-texte-muet">Une seule étape ne fait pas un entonnoir : ajoutez-en au moins une autre.</p> : null}
    </div>
  );
}
